"use client";

import { Button, Heading, Image, Text } from "@pamperedchef/design-system";

import type { CallToAction } from "../types";
import * as styles from "./PromoBanner.module.scss";

export interface PromoBannerProps {
	eyebrow?: string;
	headline: string;
	body?: string;
	footnote?: string;
	image?: { src: string; alt: string };
	action?: CallToAction;
	tone?: "neutral" | "warm";
	/** Navigation stays with the caller; composites do not touch `window`. */
	onActionPress?(action: CallToAction): void;
}

export function PromoBanner({
	eyebrow,
	headline,
	body,
	footnote,
	image,
	action,
	tone = "neutral",
	onActionPress
}: PromoBannerProps): JSX.Element {
	return (
		<section
			className={styles["promo"]}
			data-component="PromoBanner"
			data-tone={tone}
		>
			{image ? (
				<div className={styles["promo__media"]}>
					<Image
						src={image.src}
						alt={image.alt}
						className={styles["promo__image"]}
					/>
				</div>
			) : null}
			<div className={styles["promo__copy"]}>
				{eyebrow ? (
					<Text tag="p" className={styles["promo__eyebrow"]}>
						{eyebrow}
					</Text>
				) : null}
				<Heading tag="h2" className={styles["promo__headline"]}>
					{headline}
				</Heading>
				{body ? (
					<Text tag="p" className={styles["promo__body"]}>
						{body}
					</Text>
				) : null}
				{footnote ? (
					<Text tag="p" className={styles["promo__footnote"]}>
						{footnote}
					</Text>
				) : null}
			</div>
			{action ? (
				<Button
					kind="primary"
					size="lg"
					className={styles["promo__action"]}
					aria-label={action.assistiveText}
					onPress={() => onActionPress?.(action)}
				>
					{action.label}
				</Button>
			) : null}
		</section>
	);
}
